// src/views/dashboard/components/UltimoJueves.tsx
import React from "react";
import { GiSoccerBall, GiBarbecue } from "react-icons/gi";
import Card from "components/card";
import { Badge, Button } from "components/ui";
import { esMismoDia } from "utils/jueves";

export interface EventoDelJueves {
  id: number;
  tipo: string;
  cancelado: boolean;
  /** Cuántos se marcaron presentes. */
  cantidad: number;
  /** Nombres, cuando el endpoint los trae anidados. Puede venir vacío. */
  asistentes: string[];
}

interface Props {
  /** El último jueves (hoy, si hoy es jueves). */
  fecha: Date;
  /** El evento de ese día, si existe. */
  evento: EventoDelJueves | null;
  /** Plantel activo, para el denominador de la convocatoria. */
  totalJugadores: number;
  /** Sólo con sesión iniciada se ofrece cargar o editar. */
  puedeEditar: boolean;
  onCargar: () => void;
  /** Aún no llegaron los datos: se muestra el esqueleto. */
  cargando?: boolean;
}

/** Cuántos nombres se listan antes de resumir en "+N". */
const MAX_CHIPS = 12;

/*
 * Los nombres van a mano en vez de con `date-fns/locale`: son doce palabras que
 * no van a cambiar, y el paquete de locales se publica como ESM puro, que el
 * runner de tests de CRA no sabe transformar.
 */
const DIAS = [
  "domingo",
  "lunes",
  "martes",
  "miércoles",
  "jueves",
  "viernes",
  "sábado",
];
const MESES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

const dosDigitos = (n: number) => String(n).padStart(2, "0");

/**
 * Franja de estado del último jueves.
 *
 * Es lo primero de la pantalla porque contesta las dos preguntas con las que
 * se abre la app: "¿cargué el jueves?" y "¿quiénes fueron?". Antes eso vivía
 * dos tarjetas más abajo, detrás de un clic para expandir.
 */
const UltimoJueves: React.FC<Props> = ({
  fecha,
  evento,
  totalJugadores,
  puedeEditar,
  onCargar,
  cargando = false,
}) => {
  const esHoy = esMismoDia(fecha, new Date());
  const fechaLarga = `${DIAS[fecha.getDay()]} ${fecha.getDate()} de ${
    MESES[fecha.getMonth()]
  }`;
  const fechaCorta = `${dosDigitos(fecha.getDate())}/${dosDigitos(
    fecha.getMonth() + 1
  )}`;

  const cantidad = evento?.cantidad ?? 0;
  const asistentes = evento?.asistentes ?? [];
  const visibles = asistentes.slice(0, MAX_CHIPS);
  const restantes = asistentes.length - visibles.length;

  const estado = !evento
    ? "sin-evento"
    : evento.cancelado
    ? "cancelado"
    : cantidad === 0
    ? "sin-asistencias"
    : "cargado";

  if (cargando) {
    return (
      <Card extra="border-l-[3px] border-l-gray-200 p-4 sm:p-5 dark:border-l-white/10">
        <div className="skeleton h-4 w-40 rounded-full" />
        <div className="skeleton mt-3 h-7 w-24 rounded-full" />
        <div className="skeleton mt-3 h-5 w-full rounded-full" />
      </Card>
    );
  }

  return (
    <Card
      extra={`p-4 sm:p-5 border-l-[3px] ${
        estado === "sin-evento"
          ? "border-l-amber-400"
          : estado === "cancelado"
          ? "border-l-red-400"
          : "border-l-brand-500"
      }`}
    >
      {/* Encabezado: en mobile el estado baja a su propia línea */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h2 className="text-[15px] font-semibold text-gray-900 dark:text-white sm:text-[17px]">
          <span className="capitalize">{esHoy ? "Hoy" : fechaLarga}</span>
          {esHoy && (
            <span className="ml-1.5 font-normal text-gray-700 dark:text-gray-400">
              · jueves {fechaCorta}
            </span>
          )}
        </h2>

        {estado === "sin-evento" && <Badge tone="warning">Sin cargar</Badge>}
        {estado === "cancelado" && <Badge tone="danger">Cancelado</Badge>}
        {estado === "sin-asistencias" && (
          <Badge tone="neutral">Sin asistentes marcados</Badge>
        )}
        {estado === "cargado" && evento && (
          <Badge
            tone="brand"
            icon={
              evento.tipo === "Peña" ? (
                <GiBarbecue />
              ) : (
                <GiSoccerBall />
              )
            }
          >
            {evento.tipo}
          </Badge>
        )}
      </div>

      {/* Cuerpo */}
      {estado === "cargado" && (
        <>
          <p className="tabular mt-3 text-[26px] font-bold leading-none text-gray-900 dark:text-white sm:text-[30px]">
            {cantidad}
            <span className="ml-1.5 text-[13px] font-medium text-gray-700 dark:text-gray-400 sm:text-sm">
              de {totalJugadores || "—"}
            </span>
          </p>

          {asistentes.length > 0 && (
          <ul className="mt-3.5 flex flex-wrap gap-1.5">
            {visibles.map((nombre) => (
              <li
                key={nombre}
                className="rounded-full border border-gray-200/80 bg-gray-50 px-2.5 py-1 text-[12px] font-medium text-gray-700 dark:border-white/10 dark:bg-white/5 dark:text-gray-300"
              >
                {nombre}
              </li>
            ))}
            {restantes > 0 && (
              <li className="rounded-full border border-gray-200/80 bg-white px-2.5 py-1 text-[12px] font-medium text-gray-700 dark:border-white/10 dark:bg-transparent dark:text-gray-400">
                +{restantes}
              </li>
            )}
          </ul>
          )}
        </>
      )}

      {estado === "sin-asistencias" && (
        <p className="mt-2.5 text-sm leading-relaxed text-gray-700 dark:text-gray-400">
          El evento está creado pero todavía no se marcó quién fue.
        </p>
      )}

      {estado === "cancelado" && (
        <p className="mt-2.5 text-sm leading-relaxed text-gray-700 dark:text-gray-400">
          Ese jueves no se jugó.
        </p>
      )}

      {estado === "sin-evento" && (
        <p className="mt-2.5 text-sm leading-relaxed text-gray-700 dark:text-gray-400">
          {puedeEditar
            ? "Todavía no cargaste el evento de este jueves."
            : "Todavía no se cargó el evento de este jueves."}
        </p>
      )}

      {/* Acción: ancho completo en mobile, al ras del texto desde sm */}
      {puedeEditar && estado !== "cancelado" && (
        <div className="mt-4">
          <Button
            variant={estado === "cargado" ? "secondary" : "primary"}
            size="sm"
            fullWidth
            className="sm:w-auto"
            onClick={onCargar}
          >
            {estado === "cargado"
              ? "Editar asistencias"
              : estado === "sin-asistencias"
              ? "Marcar asistentes"
              : "Cargar el jueves"}
          </Button>
        </div>
      )}
    </Card>
  );
};

export default UltimoJueves;
